import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Grid, InputAdornment, MenuItem, Snackbar, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { formatUser, USER_LANGUAGES, UserDetails, userSchema } from "./User";
import { makeRequired } from "../../shared/components/forms/formUtils";
import { RhfForm } from "../../shared/components/forms/RhfForm";
import { FormHeader } from "../../shared/components/forms/FormHeader";
import { MuiRhfTextField } from "../../shared/components/forms/MuiRhfTextField";
import { yupResolver } from "@hookform/resolvers/yup";
import { disableUser, enableUser, getUserByEmail, sendRegistrationMailToUser } from "./UsersApi";
import { useAxios } from "../../shared/technical/rest/AxiosProvider";
import { useDebounce } from "../../shared/technical/hooks/useDebounce";
import { ApplicationRolesForm } from "./ApplicationRolesForm";
import { MuiRhfSelectField } from "../../shared/components/forms/MuiRhfSelectField";
import LanguageIcon from '@mui/icons-material/Language';
import { MuiRhfCompanySelectField } from "../companies/MuiRhfCompanySelectField";
import { useAccount } from "./AccountProvider";
import { isAdmin, isApplicationAdmin, isApplicationAdminForUser, isCompanyAdmin } from "./AccountServiceRoles";
import { MuiRhfCheckbox } from "../../shared/components/forms/MuiRhfCheckbox";
import { MuiRhfDatePicker } from "../../shared/components/forms/MuiRhfDatePicker";
import { AlertColor } from "@mui/material/Alert/Alert";
import { GroupsForm } from "./GroupsForm";
import { AcceptedTermsAndConditionsForm } from "./AcceptedTermsAndConditionsForm";
import { useApplications } from "../applications/ApplicationProvider";
import { hasFieldErrorCode, USER_EXISTS_ALREADY } from "../../shared/technical/rest/errorHandling";
import { handleFormError } from "../../shared/components/forms/RhfForm";
import { ExistingUserDialog } from "./ExistingUserDialog";
import { UserEnabledMenuEntry, UserEnabledStatus } from "./UserEnabledStatus";

export function UserForm(props: {
  user: UserDetails,
  handleSave: (user: UserDetails) => Promise<UserDetails>,
  handleDelete: (user: UserDetails) => void,
  handleClose: () => void,
  handleRefresh: () => void
}) {
  const {t} = useTranslation();
  const account = useAccount();
  const theme = useTheme();
  const axios = useAxios();
  const applications = useApplications();
  const {handleRefresh, handleDelete, handleClose, handleSave} = props;
  const [ formReadonly, setFormReadonly ] = useState<boolean>(!!props.user.id);
  const [ user, setUser ] = useState<UserDetails>(props.user);
  const [ snackBarOpen, setSnackBarOpen ] = React.useState(false);
  const [ snackBarSeverity, setSnackBarSeverity ] = React.useState<AlertColor>('info');
  const [ snackBarText, setSnackBarText ] = React.useState<string>();
  const [ existingUserToBeExtended, setExistingUserToBeExtended ] = useState<UserDetails>();
  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: user,
    resolver: yupResolver(userSchema(t))
  });
  const {reset, register, watch, setError} = methods;

  useEffect(() => {
    register('id');
    register('version');
    // Not editable in the form, but has to be sent back unchanged
    register('enabled');
  }, [ register ]);

  useEffect(() => {
    if (!user.id && isCompanyAdmin(account)) {
      // New user created by company admin => set company
      user.company = account.company
    }
    setUser(user);
  }, [ user, account ]);

  useEffect(() => {
    reset(user);
  }, [ user, reset ]);

  const emailWatch = watch('email');
  const emailTrigger = useDebounce(emailWatch, 500);
  useEffect(() => {
    if (!emailTrigger) {
      return
    }
    if (user.id) {
      return // do not check email for existing user - maybe we change that in future....
    }
    const checkForExistingUser = function () {
      getUserByEmail(axios, emailTrigger).then(user => {
        if (!user) {
          return
        }
        setUser(user);
        setFormReadonly(true);
        setSnackBarText(t('UserPage.msgExistingUserLoaded'));
        setSnackBarOpen(true);
      });
    };
    checkForExistingUser();
  }, [ emailTrigger, user, axios, t ]);

  const showSaveError = useCallback((error: any) => {
    try {
      // Shows the error at the affected form field
      handleFormError(error, setError, t);
    } catch (unhandledError) {
      console.error('Could not save user', unhandledError);
      setSnackBarSeverity('error');
      setSnackBarText(t('UserPage.msgSaveFailed'));
      setSnackBarOpen(true);
    }
  }, [ setError, t ]);

  const handleSubmitUser = useCallback(async (submittedUser: UserDetails) => {
    try {
      await handleSave(submittedUser);
    } catch (error) {
      if (hasFieldErrorCode(error, USER_EXISTS_ALREADY)) {
        // The user exists, but may be invisible for us => offer to add our applications to him.
        // The error is shown at the email field as well, so it stays visible if the dialog is cancelled.
        showSaveError(error);
        setExistingUserToBeExtended(submittedUser);
        return;
      }
      showSaveError(error);
    }
  }, [ handleSave, showSaveError ]);

  const handleAddApplicationsToExistingUser = useCallback(async () => {
    if (!existingUserToBeExtended) {
      return;
    }
    const userToBeExtended = existingUserToBeExtended;
    setExistingUserToBeExtended(undefined);
    try {
      await handleSave({...userToBeExtended, upsert: true});
    } catch (error) {
      showSaveError(error);
    }
  }, [ existingUserToBeExtended, handleSave, showSaveError ]);

  const handleSnackBarClose = (event: React.SyntheticEvent<any> | Event) => {
    setSnackBarOpen(false);
    setSnackBarSeverity('info');
  };

  const handleSendRegistrationEmail = useCallback(() => {
    sendRegistrationMailToUser(axios, user.id!!).then(() => {
      setSnackBarText(t('UserPage.msgRegistrationEmailSent'));
      setSnackBarOpen(true);
    })
  }, [ axios, t, user ]);

  const handleEnableDisableUser = useCallback(() => {
    const disabling = user.enabled;
    const request = disabling ? disableUser(axios, user.id!!) : enableUser(axios, user.id!!);
    request.then((updatedUser) => {
      setSnackBarSeverity('info');
      setSnackBarText(t(disabling ? 'UserPage.msgUserDisabled' : 'UserPage.msgUserEnabled'));
      setSnackBarOpen(true);
      handleRefresh();
      setUser(updatedUser);
    }).catch(() => {
      // The only expected error is an activation that the validity period prevents
      setSnackBarSeverity('error');
      setSnackBarText(t('UserPage.msgUserNotEnabledBecauseOfValidity'));
      setSnackBarOpen(true);
    })
  }, [ axios, t, user, handleRefresh ]);

  const stdFieldProps = {
    disabled: formReadonly,
  }
  const inputLabelProps = {
    InputLabelProps: {
      shrink: true,
    }
  }
  const isAdminAndCurrentUser = account.id === user.id && isAdmin(account)
  const required = makeRequired(userSchema(t));
  const isNew = !(!!user.id)
  const canEnableDisableUser = !isNew &&
    (!isApplicationAdmin(account) || isApplicationAdminForUser(account, user, applications))
  const editFormTitle = isNew ? t('User.titleNew') : t('User.titleEdit') + ': ' + formatUser(user);
  return (
    <FormProvider { ...methods } >
      <RhfForm id={ 'user-form' } onSubmit={ handleSubmitUser }>
        <FormHeader title={ editFormTitle }
                    onCancel={ handleClose }
                    onDelete={ () => handleDelete(user) }
                    canBeEdited={ true }
                    deleteIsDisabled={ isAdminAndCurrentUser }
                    readonly={ formReadonly }
                    onStartEditing={ () => setFormReadonly(false) }
                    additionalMenuEntry={
                      <>
                        <MenuItem onClick={ handleSendRegistrationEmail } disabled={ !user.enabled }>
                          { t('UserPage.menuSendRegistrationMail') }
                        </MenuItem>
                        { canEnableDisableUser &&
                          <UserEnabledMenuEntry user={ user } onToggle={ handleEnableDisableUser } />
                        }
                      </>
                    }
        />
        <Grid container={ true } sx={ {marginTop: theme.spacing(5), marginBottom: theme.spacing(2), width: 'auto'} }>
          <Grid item={ true } lg={ 12 }>
            <Grid container={ true } spacing={ 3 }>
              <Grid item={ true } xs={ 12 }>
                <MuiRhfTextField name={ 'email' }
                                 label={ t('User.email') }
                                 fullWidth={ true }
                                 required={ required?.email }
                                 variant="outlined"
                                 { ...stdFieldProps }
                                 { ...inputLabelProps }
                />
              </Grid>
              <Grid item={ true } xs={ 12 } lg={ 6 }>
                <MuiRhfTextField name={ 'firstName' }
                                 label={ t('User.firstName') }
                                 fullWidth={ true }
                                 variant="outlined"
                                 required={ required?.firstName }
                                 { ...stdFieldProps }
                                 { ...inputLabelProps } />
              </Grid>
              <Grid item={ true } xs={ 12 } lg={ 6 }>
                <MuiRhfTextField name={ 'lastName' }
                                 label={ t('User.lastName') }
                                 fullWidth={ true }
                                 variant="outlined"
                                 required={ required?.lastName }
                                 { ...stdFieldProps }
                                 { ...inputLabelProps } />
              </Grid>
              <Grid item={ true } xs={ 8 } lg={ 6 }>
                <MuiRhfSelectField name={ 'locale' }
                                   list={ USER_LANGUAGES }
                                   getListKey={ (locale) => locale }
                                   getListLabel={ (locale) => t('Locales.' + locale) }
                                   label={ t('User.locale') }
                                   startAdornment={
                                     <InputAdornment position="start">
                                       <LanguageIcon color="action" />
                                     </InputAdornment>
                                   }
                                   fullWidth={ true }
                                   required={ required?.locale }
                                   { ...stdFieldProps } />
              </Grid>
              <Grid item={ true } xs={ 8 } lg={ 6 }>
                <MuiRhfCompanySelectField name={ 'company' } label={ t('User.company') }
                                          disabled={ formReadonly || isCompanyAdmin(account) }
                                          required={ required?.company } />
              </Grid>
              <Grid item={ true } xs={ 8 } lg={ 3 }>
                <UserEnabledStatus user={ user } />
              </Grid>
              <Grid item={ true } xs={ 8 } lg={ 3 }>
                { /* Vertically centered like the status chip, so both share one baseline with the date pickers (SSO-299) */ }
                <Box sx={ {display: 'flex', alignItems: 'center', height: '100%'} }>
                  <MuiRhfCheckbox name={ 'emailVerified' } label={ t('User.emailVerified') } required={ required?.emailVerified }
                                  disabled={ true } />
                </Box>
              </Grid>
              <Grid item={ true } xs={ 8 } lg={ 3 }>
                <MuiRhfDatePicker name={ 'validityInfo.validFrom' } label={ t('ValidityInfo.validFrom') }
                                  required={ required?.validityInfo.validFrom }
                                  disabled={ formReadonly || (!isAdmin(account) && !isApplicationAdmin(account)) } />
              </Grid>
              <Grid item={ true } xs={ 8 } lg={ 3 }>
                <MuiRhfDatePicker name={ 'validityInfo.validTo' } label={ t('ValidityInfo.validTo') }
                                  required={ required?.validityInfo.validTo }
                                  disabled={ formReadonly || (!isAdmin(account) && !isApplicationAdmin(account)) } />
              </Grid>
              <Grid item={ true } xs={ 12 }>
                <ApplicationRolesForm disabled={ formReadonly } />
              </Grid>
              <Grid item={ true } xs={ 12 }>
                <GroupsForm disabled={ formReadonly } />
              </Grid>
              <Grid item={ true } xs={ 12 }>
                <AcceptedTermsAndConditionsForm disabled={ formReadonly } />
              </Grid>
            </Grid>
            <Snackbar
              anchorOrigin={ {
                vertical: 'bottom',
                horizontal: 'center',
              } }
              open={ snackBarOpen }
              autoHideDuration={ 5000 }
              onClose={ handleSnackBarClose }
            >
              <Alert severity={ snackBarSeverity } onClose={ handleSnackBarClose }>{ snackBarText }</Alert>
            </Snackbar>
            <ExistingUserDialog open={ !!existingUserToBeExtended }
                                onClose={ () => setExistingUserToBeExtended(undefined) }
                                onConfirm={ handleAddApplicationsToExistingUser } />
          </Grid>
        </Grid>
      </RhfForm>
    </FormProvider>
  );
}
